import api from './api';
import apiCacheService from './ApiCacheService';
import apiMonitor from '../utils/apiMonitor';

/**
 * SnapshotDataService - Optimized for snapshot/historical data
 * Handles all API calls for historical campaign snapshots with proper error handling
 * Based on actual SystemMonthlySnapshot.snapshot JSON structure
 */
class SnapshotDataService {
  /**
   * Get enhanced dashboard metrics from snapshot
   * Uses the enhanced-metrics endpoint with snapshot_month parameter
   * @param {string} month - Snapshot month in YYYY-MM format
   * @returns {Promise} Enhanced metrics from snapshot data
   */
  static async getEnhancedMetrics(month) {
    try {
      console.log(`📊 Fetching enhanced metrics for snapshot month: ${month}`);
      const response = await api.get(`/dashboard/enhanced-metrics/?snapshot_month=${month}`);
      console.log('✅ Enhanced metrics response:', response.data);
      return response;
    } catch (error) {
      console.error('❌ Error fetching snapshot enhanced metrics:', error);
      // Return fallback structure instead of throwing
      return {
        data: {
          financial_metrics: {
            total_budget: 0,
            total_planning: 0,
            total_actual: 0,
            total_savings: 0,
            total_campaigns: 0
          },
          brand_performance: [],
          performance_ratios: {
            budget_utilization: 0,
            spend_efficiency: 0,
            savings_rate: 0,
            campaign_health_score: 0
          },
          alerts: [],
          data_type: 'snapshot_fallback',
          snapshot_month: month
        }
      };
    }
  }

  /**
   * Get budget data from snapshot
   * Uses the budget-data endpoint with snapshot_month parameter
   * @param {string} month - Snapshot month in YYYY-MM format
   * @returns {Promise} Budget data from snapshot
   */
  static async getBudgetData(month) {
    try {
      console.log(`💰 Fetching budget data for snapshot month: ${month}`);
      const response = await api.get('/dashboard/budget-data/', {
        params: { snapshot_month: month }
      });
      console.log('✅ Snapshot budget data fetched successfully:', response.data?.length || 0, 'brands');
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching snapshot budget data:', error);
      return [];
    }
  }

  /**
   * Get enhanced dashboard metrics from new snapshot structure (3 tables)
   * @param {string} month - Snapshot month in YYYY-MM format
   * @returns {Promise} Enhanced metrics from new snapshot structure
   */
  static async getNewSnapshotEnhancedMetrics(month) {
    try {
      console.log(`📊 Fetching new snapshot enhanced metrics for month: ${month}`);
      
      // Prefer snapshot_month so backend can resolve to latest snapshot_date within that month
      const response = await api.get(`/snapshot-dashboard/enhanced-metrics/?snapshot_month=${month}`);
      console.log('✅ New snapshot enhanced metrics response:', response.data);
      return response;
    } catch (error) {
      console.error('❌ Error fetching new snapshot enhanced metrics:', error);
      
      // Check if it's a 404 error (no snapshot data)
      if (error.response && error.response.status === 404) {
        console.warn(`⚠️ No snapshot data available for month: ${month}`);
        throw new Error(`No snapshot data available for ${month}. Please select a different month.`);
      }
      
      // Check if it's a 500 error (API error)
      if (error.response && error.response.status === 500) {
        console.error(`❌ API error for month ${month}:`, error.response.data);
        throw new Error(`Failed to retrieve snapshot data for ${month}. Please try again later.`);
      }
      
      // Return fallback structure for other errors
      return {
        data: {
          financial_metrics: {
            total_budget: 0,
            total_planning: 0,
            total_actual: 0,
            total_savings: 0,
            total_campaigns: 0
          },
          brand_performance: [],
          performance_ratios: {
            budget_utilization: 0,
            spend_efficiency: 0,
            savings_rate: 0,
            campaign_health_score: 0
          },
          alerts: [],
          data_type: 'new_snapshot_fallback',
          snapshot_date: month
        }
      };
    }
  }

  /**
   * Get brand summaries from new snapshot structure
   * @param {string} month - Snapshot month in YYYY-MM format
   * @returns {Promise} Brand summaries from new snapshot structure
   */
  static async getNewSnapshotBrandSummaries(month) {
    try {
      console.log(`📊 Fetching new snapshot brand summaries for month: ${month}`);
      
      // Prefer snapshot_month so backend can resolve to latest snapshot_date within that month
      const response = await api.get(`/snapshot-dashboard/brand-summaries/?snapshot_month=${month}`);
      console.log('✅ New snapshot brand summaries response:', response.data);
      
      // Transform data to match component expectations
      if (response.data && response.data.brand_summaries) {
        const transformedBrandSummaries = response.data.brand_summaries.map(brand => ({
          ...brand,
          // Map brand_name correctly
          brand_name: brand.brand_name || 'Unknown Brand',
          campaigns: (brand.campaigns || []).map(campaign => ({
            ...campaign,
            // Fix campaign field mapping
            campaign_id: campaign.id,
            planning_cost: campaign.planning_total || 0,
            actual_cost: campaign.actual_total || 0,
            given_cost: campaign.total_given || 0,
            media_types: (campaign.media_types || []).map(mediaType => ({
              ...mediaType,
              // Transform monthly_data to monthly_breakdown
              monthly_breakdown: (mediaType.monthly_data || []).map(monthData => ({
                month: monthData.month,
                planning_cost: monthData.planning_gross_cost || 0,
                actual_cost: monthData.actual_gross_cost || 0,
                given_cost: monthData.given || 0,
                // Map regional data for Tab 1
                ntw: monthData.ntw || 0,
                central_mid: monthData.central_mid || 0,
                the_rest_of_central: monthData.the_rest_of_central || 0,
                ghcm: monthData.ghcm || 0,
                mekong: monthData.mekong || 0,
                north: monthData.north || 0,
                // Additional fields for compatibility
                ytd: monthData.actual_gross_cost || 0,
                ytg: Math.max(0, (monthData.planning_gross_cost || 0) - (monthData.actual_gross_cost || 0)),
                saving: monthData.final_saving || 0,
                provisional_saving: monthData.provisional_saving || 0,
                is_future_summary: false,
                campaign_name: campaign.campaign_name,
                brand_name: brand.brand_name
              }))
            }))
          }))
        }));

        return {
          ...response,
          data: {
            ...response.data,
            brand_summaries: transformedBrandSummaries
          }
        };
      }
      
      return response;
    } catch (error) {
      console.error('❌ Error fetching new snapshot brand summaries:', error);
      return {
        data: {
          brand_summaries: [],
          total_brands: 0,
          snapshot_date: month
        }
      };
    }
  }

  /**
   * Get OOH campaigns from new snapshot structure
   * @param {string} month - Snapshot month in YYYY-MM format
   * @returns {Promise} OOH campaigns from new snapshot structure
   */
  static async getNewSnapshotOOHCampaigns(month) {
    try {
      console.log(`📊 Fetching new snapshot OOH campaigns for month: ${month}`);
      
      // Prefer snapshot_month so backend can resolve to latest snapshot_date within that month
      const response = await api.get(`/snapshot-dashboard/ooh-campaigns/?snapshot_month=${month}`);
      console.log('✅ New snapshot OOH campaigns response:', response.data);
      return response;
    } catch (error) {
      console.error('❌ Error fetching new snapshot OOH campaigns:', error);
      return {
        data: {
          ooh_campaigns: [],
          summary: {
            total_campaigns: 0,
            total_budget: 0,
            total_planning: 0,
            total_actual: 0,
            total_savings: 0
          },
          region_breakdown: [],
          snapshot_date: month
        }
      };
    }
  }

  /**
   * Get monthly data from new snapshot structure
   * @param {string} month - Snapshot month in YYYY-MM format
   * @returns {Promise} Monthly data from new snapshot structure
   */
  static async getNewSnapshotMonthlyData(month) {
    try {
      console.log(`📊 Fetching new snapshot monthly data for month: ${month}`);
      
      // Prefer snapshot_month so backend can resolve to latest snapshot_date within that month
      const response = await api.get(`/snapshot-dashboard/monthly-data/?snapshot_month=${month}`);
      console.log('✅ New snapshot monthly data response:', response.data);
      return response;
    } catch (error) {
      console.error('❌ Error fetching new snapshot monthly data:', error);
      return {
        data: {
          monthly_breakdown: [],
          total_months: 0,
          snapshot_date: month
        }
      };
    }
  }

  /**
   * Get brand summaries from snapshot
   * @param {string} month - Snapshot month in YYYY-MM format
   * @returns {Promise} Brand summaries from snapshot
   */
  static async getBrandSummaries(month) {
    try {
      console.log(`📊 Fetching brand summaries for snapshot month: ${month}`);
      const response = await api.get(`/reports/brand-summaries/snapshot/summary?month=${month}`);
      console.log('✅ Brand summaries response:', response.data);
      return response;
    } catch (error) {
      console.error('❌ Error fetching snapshot brand summaries:', error);
      // Try alternative endpoint
      try {
        const altResponse = await api.get(`/reports/brand-summaries/?snapshot_month=${month}`);
        console.log('✅ Brand summaries (alternative endpoint):', altResponse.data);
        return altResponse;
      } catch (altError) {
        console.error('❌ Alternative brand summaries endpoint also failed:', altError);
        return { data: [] };
      }
    }
  }

  /**
   * Get performance metrics from snapshot
   * @param {string} month - Snapshot month in YYYY-MM format
   * @returns {Promise} Performance metrics from snapshot
   */
  static async getPerformanceMetrics(month) {
    try {
      console.log(`📊 Fetching performance metrics for snapshot month: ${month}`);
      const response = await api.get(`/reports/savings-detail/?month=${month}`);
      console.log('✅ Performance metrics response:', response.data);
      return response;
    } catch (error) {
      console.error('❌ Error fetching snapshot performance metrics:', error);
      return { data: [] };
    }
  }

  /**
   * Get OOH campaigns from snapshot
   * @param {string} month - Snapshot month in YYYY-MM format
   * @returns {Promise} OOH campaigns from snapshot
   */
  static async getOOHCampaigns(month) {
    try {
      console.log(`📊 Fetching OOH campaigns for snapshot month: ${month}`);
      const response = await api.get(`/reports/ooh-campaigns/snapshot/?month=${month}`);
      console.log('✅ OOH campaigns response:', response.data);
      return response;
    } catch (error) {
      console.error('❌ Error fetching snapshot OOH campaigns:', error);
      // Try alternative endpoint
      try {
        const altResponse = await api.get(`/reports/ooh-campaigns/?snapshot_month=${month}`);
        console.log('✅ OOH campaigns (alternative endpoint):', altResponse.data);
        return altResponse;
      } catch (altError) {
        console.error('❌ Alternative OOH campaigns endpoint also failed:', altError);
        return { data: [] };
      }
    }
  }

  /**
   * Get detailed campaign data with monthly breakdown from snapshot
   * @param {string} month - Snapshot month in YYYY-MM format
   * @returns {Promise} Detailed campaign data with monthly breakdown from snapshot
   */
  static async getDetailedCampaigns(month) {
    try {
      console.log(`📊 Fetching detailed campaigns for snapshot month: ${month}`);
      const response = await api.get(`/campaigns-data/?snapshot_month=${month}`);
      console.log('✅ Detailed campaigns response:', response.data);
      return response;
    } catch (error) {
      console.error('❌ Error fetching snapshot detailed campaigns:', error);
      // Try alternative endpoint
      try {
        const altResponse = await api.get(`/campaigns/?snapshot_month=${month}`);
        console.log('✅ Detailed campaigns (alternative endpoint):', altResponse.data);
        return altResponse;
      } catch (altError) {
        console.error('❌ Alternative detailed campaigns endpoint also failed:', altError);
        return { data: [] };
      }
    }
  }

  /**
   * Get regional breakdown data from snapshot
   * @param {string} month - Snapshot month in YYYY-MM format
   * @param {Object} filters - Filter parameters (brand, year, campaign)
   * @returns {Promise} Regional breakdown data from snapshot
   */
  static async getSnapshotRegionalBreakdown(month, filters = {}) {
    try {
      console.log(`🌏 Fetching snapshot regional breakdown for month: ${month}`, filters);
      
      const params = new URLSearchParams();
      params.append('snapshot_month', month);
      
      if (filters.brand && filters.brand !== 'all') {
        params.append('brand', filters.brand);
      }
      
      if (filters.year) {
        params.append('year', filters.year);
      }
      
      if (filters.campaign && filters.campaign !== 'all') {
        params.append('campaign', filters.campaign);
      }
      
      const queryString = params.toString();
      const url = `/reports/campaign-regional-breakdown/?${queryString}`;
      
      console.log('🌏 Fetching snapshot regional data from:', url);
      const response = await api.get(url);
      
      console.log('✅ Snapshot regional breakdown response:', {
        dataCount: response.data?.data?.length || 0,
        summary: response.data?.summary
      });
      
      return response;
    } catch (error) {
      console.error('❌ Error fetching snapshot regional breakdown:', error);
      
      // Try to extract regional data from raw snapshot data as fallback
      try {
        console.log('🔄 Attempting fallback: extracting regional data from raw snapshot');
        const rawResponse = await this.getRawSnapshotData(month);
        
        if (rawResponse?.data?.snapshot) {
          const regionalData = this.extractRegionalDataFromSnapshot(rawResponse.data.snapshot, filters);
          console.log('✅ Successfully extracted regional data from snapshot:', regionalData.length);
          return { data: { data: regionalData } };
        }
      } catch (fallbackError) {
        console.error('❌ Fallback regional data extraction also failed:', fallbackError);
      }
      
      return { data: { data: [] } };
    }
  }

  /**
   * Extract regional data from raw snapshot JSON
   * Fallback method when dedicated regional API is not available
   * @param {Object} snapshotData - Raw snapshot data
   * @param {Object} filters - Filter parameters
   * @returns {Array} Processed regional data
   */
  static extractRegionalDataFromSnapshot(snapshotData, filters = {}) {
    try {
      let campaigns = [];
      
      // Handle different snapshot data formats
      if (snapshotData.campaigns) {
        campaigns = snapshotData.campaigns;
      } else if (Array.isArray(snapshotData)) {
        campaigns = snapshotData;
      } else {
        console.warn('⚠️ Unknown snapshot data format for regional extraction');
        return [];
      }

      console.log(`🔍 Extracting regional data from ${campaigns.length} campaigns`);
      
      const regionalBreakdown = [];
      
      campaigns.forEach(campaign => {
        const campaignName = campaign.campaign_name || campaign.Campaign_Name || 'Unknown Campaign';
        const brandName = campaign.brand_name || campaign.Brand || 'Unknown Brand';
        
        // Apply filters
        if (filters.brand && filters.brand !== 'all' && 
            !brandName.toLowerCase().includes(filters.brand.toLowerCase())) {
          return;
        }
        
        if (filters.campaign && filters.campaign !== 'all' && 
            !campaignName.toLowerCase().includes(filters.campaign.toLowerCase())) {
          return;
        }
        
        // Process media types to get monthly data
        const mediaTypes = campaign.media_types || {};
        const mediaTypeArray = Object.values(mediaTypes);
        
        mediaTypeArray.forEach(mediaType => {
          const monthlyData = mediaType.monthly_data || [];
          
          monthlyData.forEach(monthData => {
            const month = monthData.month || monthData.Month;
            
            // Apply year filter
            if (filters.year && month) {
              const year = month.split('-')[0];
              if (year !== filters.year) {
                return;
              }
            }
            
            // Extract costs
            const givenCost = parseFloat(monthData.given || monthData.given_cost || monthData.Given_Cost_Excl_VAT_VND || 0);
            const planningCost = parseFloat(monthData.planning_gross_cost || monthData.Planning_Gross_Cost_Excl_VAT_VND || 0);
            const actualCost = parseFloat(monthData.actual_gross_cost || monthData.Actual_Gross_Cost_Excl_VAT_VND || 0);
            
            // Get regional breakdown fields
            const regionalFields = {
              ntw: parseFloat(monthData.ntw || 0),
              north: parseFloat(monthData.north || 0),
              central_mid: parseFloat(monthData.central_mid || 0),
              the_rest_of_central: parseFloat(monthData.the_rest_of_central || 0),
              ghcm: parseFloat(monthData.ghcm || 0),
              mekong: parseFloat(monthData.mekong || 0)
            };
            
            // Calculate total from regions for distribution if regional data exists
            const regionalTotal = regionalFields.north + regionalFields.central_mid + 
                                regionalFields.the_rest_of_central + regionalFields.ghcm + regionalFields.mekong;
                                
            // If no regional breakdown, distribute total proportionally
            const regions = ['North', 'Central Mid', 'The Rest of Central', 'GHCM', 'Mekong'];
            const regionPercentages = [0.25, 0.20, 0.15, 0.25, 0.15]; // Distribution percentages
            
            regions.forEach((regionName, index) => {
              let regionValue;
              
              if (regionalTotal > 0) {
                // Use actual regional breakdown
                const fieldName = regionName.toLowerCase().replace(/\s+/g, '_').replace('the_rest_of_central', 'the_rest_of_central');
                regionValue = regionalFields[fieldName] || 0;
              } else {
                // Distribute total proportionally
                regionValue = givenCost * regionPercentages[index];
              }
              
              if (regionValue > 0 || givenCost > 0 || planningCost > 0 || actualCost > 0) {
                regionalBreakdown.push({
                  campaign_id: campaign.id || campaign.campaign_id || `${brandName}_${campaignName}`,
                  campaign_name: campaignName,
                  brand_name: brandName,
                  month: month,
                  region: regionName,
                  given_cost: regionValue || (givenCost * regionPercentages[index]),
                  planning_cost: regionValue || (planningCost * regionPercentages[index]),
                  actual_cost: regionValue || (actualCost * regionPercentages[index])
                });
              }
            });
            
            // Add NTW (nationwide total) row
            if (givenCost > 0 || planningCost > 0 || actualCost > 0) {
              regionalBreakdown.push({
                campaign_id: campaign.id || campaign.campaign_id || `${brandName}_${campaignName}`,
                campaign_name: campaignName,
                brand_name: brandName,
                month: month,
                region: 'NTW (Nationwide)',
                given_cost: givenCost,
                planning_cost: planningCost,
                actual_cost: actualCost
              });
            }
          });
        });
      });
      
      console.log(`✅ Extracted ${regionalBreakdown.length} regional data points from snapshot`);
      return regionalBreakdown;
      
    } catch (error) {
      console.error('❌ Error extracting regional data from snapshot:', error);
      return [];
    }
  }

  /**
   * Get available snapshot months
   * @returns {Promise<Array>} Array of available snapshot months
   */
  static async getSnapshotMonths(forceRefresh = false) {
    const cacheKey = 'snapshot-months';
    const cacheTTL = 10 * 60 * 1000; // 10 minutes cache for snapshot months

    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cachedData = apiCacheService.getCached(cacheKey);
      if (cachedData) {
        return cachedData;
      }

      // Check if request is already pending
      if (apiCacheService.isRequestPending(cacheKey)) {
        console.log('⏳ Snapshot months request already pending, waiting...');
        return await apiCacheService.getPendingRequest(cacheKey);
      }
    }

    // Create new request
    const requestPromise = this._fetchSnapshotMonths();
    apiCacheService.addPendingRequest(cacheKey, requestPromise);

    try {
      const result = await requestPromise;
      apiCacheService.setCached(cacheKey, result, cacheTTL);
      return result;
    } finally {
      apiCacheService.removePendingRequest(cacheKey);
    }
  }

  /**
   * Internal method to fetch snapshot months from API
   * @private
   */
  static async _fetchSnapshotMonths() {
    const startTime = Date.now();
    const url = '/snapshot-dashboard/available-months/';
    
    try {
      console.log('📅 Fetching available snapshot months from new API');
      const response = await api.get(url);
      const duration = Date.now() - startTime;
      
      console.log('✅ Snapshot months response:', response.data);
      apiMonitor.logCall(url, 'GET', duration, true);
      
      return response.data.months || [];
    } catch (error) {
      const duration = Date.now() - startTime;
      apiMonitor.logCall(url, 'GET', duration, false);
      
      console.warn('⚠️ New snapshot months endpoint failed, trying legacy endpoints');
      
      // Try legacy endpoints
      const legacyUrls = [
        '/system/snapshot/months/',
        '/reports/snapshot-months/'
      ];
      
      for (const legacyUrl of legacyUrls) {
        const altStartTime = Date.now();
        try {
          const altResponse = await api.get(legacyUrl);
          const altDuration = Date.now() - altStartTime;
          
          console.log(`✅ Snapshot months (${legacyUrl}):`, altResponse.data);
          apiMonitor.logCall(legacyUrl, 'GET', altDuration, true);
          
          return altResponse.data;
        } catch (altError) {
          const altDuration = Date.now() - altStartTime;
          apiMonitor.logCall(legacyUrl, 'GET', altDuration, false);
          console.warn(`⚠️ ${legacyUrl} failed:`, altError.message);
        }
      }
      
      console.warn('⚠️ All endpoints failed, using fallback months');
      
      // Fallback: Return predefined snapshot months (updated based on database)
      const fallbackMonths = [
        { value: '2025-12', label: 'December 2025', available: true },
        { value: '2025-11', label: 'November 2025', available: true },
        { value: '2025-10', label: 'October 2025', available: true },
        { value: '2025-09', label: 'September 2025', available: true },
        { value: '2025-08', label: 'August 2025', available: true },
        { value: '2025-07', label: 'July 2025', available: true },
        { value: '2025-06', label: 'June 2025', available: true },
        { value: '2025-05', label: 'May 2025', available: true },
        { value: '2025-04', label: 'April 2025', available: true },
        { value: '2025-03', label: 'March 2025', available: true },
        { value: '2025-02', label: 'February 2025', available: true },
        { value: '2025-01', label: 'January 2025', available: true }
      ];

      console.log('📅 Using fallback snapshot months:', fallbackMonths);
      return fallbackMonths;
    }
  }

  /**
   * Process raw snapshot data to match frontend expectations
   * Handles the actual SystemMonthlySnapshot.snapshot JSON structure
   * @param {Object} snapshotData - Raw snapshot data from backend
   * @param {string} month - Snapshot month for context
   * @returns {Object} Processed snapshot data
   */
  static processSnapshotData(snapshotData, month) {
    try {
      console.log('🔄 Processing snapshot data:', snapshotData);
      
      // Handle different possible formats
      let campaigns = [];
      if (snapshotData.campaigns) {
        campaigns = snapshotData.campaigns;
      } else if (Array.isArray(snapshotData)) {
        campaigns = snapshotData;
      } else if (snapshotData.data && snapshotData.data.campaigns) {
        campaigns = snapshotData.data.campaigns;
      }

      if (!Array.isArray(campaigns)) {
        console.warn('⚠️ Campaigns data is not an array:', typeof campaigns);
        campaigns = [];
      }

      const brandMap = new Map();
      let totalBudget = 0;
      let totalPlanning = 0;
      let totalActual = 0;
      let totalCampaigns = campaigns.length;

      // Process each campaign
      // Track media type and vendor savings for charts
      const mediaTypeSavings = new Map();
      const digitalVendorSavings = new Map();
      
      // Parse snapshot month for campaign status calculation
      const snapshotDate = new Date(`${month}-01`); // Convert "2025-03" to "2025-03-01"
      console.log('📅 Snapshot date for status calculation:', snapshotDate);

      campaigns.forEach(campaign => {
        const brandName = campaign.brand_name || campaign.Brand || 'Unknown';
        
        // Initialize brand if not exists
        if (!brandMap.has(brandName)) {
          brandMap.set(brandName, {
            brand: brandName,
            brand_name: brandName,
            totalCampaigns: 0,
            totalBudget: 0,
            totalPlanning: 0,
            totalActual: 0,
            campaigns: []
          });
        }

        const brand = brandMap.get(brandName);
        brand.totalCampaigns++;

        // Use campaign-level totals if available (from snapshot)
        let campaignBudget = this.safeParseFloat(campaign.total_given || 0);
        let campaignPlanning = this.safeParseFloat(campaign.planning_total || 0);
        let campaignActual = this.safeParseFloat(campaign.actual_total || 0);
        let campaignSavings = this.safeParseFloat(campaign.saving_total || 0);

        // If campaign-level totals are 0, calculate from media types
        if (campaignBudget === 0 && campaign.media_types) {
          const mediaTypes = campaign.media_types || {};
          const mediaTypesArray = Array.isArray(mediaTypes) 
            ? mediaTypes 
            : Object.values(mediaTypes);

          mediaTypesArray.forEach(mt => {
            if (!mt || typeof mt !== 'object') return;

            const mediaTypeGiven = this.safeParseFloat(mt.media_type_given || 0);
            campaignBudget += mediaTypeGiven;

            // Process monthly data for this media type
            let mtPlanning = 0;
            let mtActual = 0;
            const monthlyData = mt.monthly_data || [];
            
            if (Array.isArray(monthlyData)) {
              monthlyData.forEach(md => {
                const planning = this.safeParseFloat(md.planning_gross_cost || md.Planning_Gross_Cost_Excl_VAT_VND || 0);
                const actual = this.safeParseFloat(md.actual_gross_cost || md.Actual_Gross_Cost_Excl_VAT_VND || 0);
                
                mtPlanning += planning;
                mtActual += actual;
              });
            } else {
              // Handle media type level data (no monthly breakdown)
              mtPlanning = this.safeParseFloat(mt.planning_gross_cost || mt.Planning_Gross_Cost_Excl_VAT_VND || 0);
              mtActual = this.safeParseFloat(mt.actual_gross_cost || mt.Actual_Gross_Cost_Excl_VAT_VND || 0);
            }

            campaignPlanning += mtPlanning;
            campaignActual += mtActual;

            // Track media type savings for pie chart
            const mtSavings = mtPlanning - mtActual;
            const mediaTypeKey = mt.media_type || 'Other';
            if (!mediaTypeSavings.has(mediaTypeKey)) {
              mediaTypeSavings.set(mediaTypeKey, 0);
            }
            mediaTypeSavings.set(mediaTypeKey, mediaTypeSavings.get(mediaTypeKey) + Math.max(0, mtSavings));

            // Track digital vendor savings for pie chart
            if (mt.digital_vendor && (mt.media_type || '').toLowerCase().includes('digital')) {
              const vendor = mt.digital_vendor;
              if (!digitalVendorSavings.has(vendor)) {
                digitalVendorSavings.set(vendor, 0);
              }
              digitalVendorSavings.set(vendor, digitalVendorSavings.get(vendor) + Math.max(0, mtSavings));
            }
          });

          // Recalculate savings if not provided
          if (campaignSavings === 0) {
            campaignSavings = campaignPlanning - campaignActual;
          }
        }

        // Add to brand totals
        brand.totalBudget += campaignBudget;
        brand.totalPlanning += campaignPlanning;
        brand.totalActual += campaignActual;

        // Add to global totals
        totalBudget += campaignBudget;
        totalPlanning += campaignPlanning;
        totalActual += campaignActual;

        // Calculate campaign status based on snapshot month vs campaign dates
        let campaignStatus = campaign.campaign_status || 'planning'; // Default
        
        const startDateStr = campaign.start_date || campaign.Start_Date;
        const endDateStr = campaign.end_date || campaign.End_Date;
        
        if (startDateStr && endDateStr) {
          const startDate = new Date(startDateStr);
          const endDate = new Date(endDateStr);
          
          if (snapshotDate < startDate) {
            campaignStatus = 'planning'; // Chưa bắt đầu
          } else if (snapshotDate > endDate) {
            campaignStatus = 'done'; // Đã kết thúc
          } else {
            campaignStatus = 'on-going'; // Đang chạy
          }
          
          console.log(`📊 Campaign "${campaign.campaign_name}": ${startDateStr} to ${endDateStr} vs ${month} → ${campaignStatus}`);
        }

        // Add processed campaign to brand
        brand.campaigns.push({
          ...campaign,
          totalBudget: campaignBudget,
          totalPlanning: campaignPlanning,
          totalActual: campaignActual,
          totalSavings: campaignSavings,
          campaign_status: campaignStatus // Override với status tính toán
        });
      });

      const brandSummaries = Array.from(brandMap.values());
      
      // Tính saving đúng cách: chỉ từ monthly_status='actual'
      let totalSavings = 0;
      campaigns.forEach(campaign => {
        const mediaTypes = campaign.media_types || {};
        const mediaTypesArray = Array.isArray(mediaTypes) ? mediaTypes : Object.values(mediaTypes);
        
        mediaTypesArray.forEach(mt => {
          if (!mt || typeof mt !== 'object') return;
          
          const monthlyData = mt.monthly_data || [];
          if (Array.isArray(monthlyData)) {
            monthlyData.forEach(md => {
              if (md.monthly_status === 'actual') {
                totalSavings += this.safeParseFloat(md.provisional_saving || 0);
              }
            });
          }
        });
      });

      console.log('✅ Processed snapshot data:', {
        totalCampaigns,
        brands: brandSummaries.length,
        totalBudget,
        totalPlanning,
        totalActual,
        totalSavings
      });

      // Prepare chart data with correct keys for CurrentEnhancedOverviewTab
      const costBreakdownData = brandSummaries.map(brand => ({
        brand: brand.brand_name,
        given: brand.totalBudget,
        planning: brand.totalPlanning,
        actual: brand.totalActual,
        saving: brand.totalPlanning - brand.totalActual
      }));

      const mediaTypePieData = Array.from(mediaTypeSavings.entries()).map(([type, savings]) => ({
        mediaType: type,
        saving: savings
      }));

      const digitalVendorPieData = Array.from(digitalVendorSavings.entries()).map(([vendor, savings]) => ({
        vendor: vendor,
        saving: savings
      }));

      return {
        campaigns,
        brandSummaries,
        processedOverview: {
          summaryCards: {
            givenCost: totalBudget,
            planningCost: totalPlanning,
            actualCost: totalActual,
            totalSavings: totalSavings,
            totalCampaigns: totalCampaigns
          },
          costBreakdownData,
          mediaTypeSavings: mediaTypePieData,
          digitalVendorSavings: digitalVendorPieData,
          performanceTableData: [],
          performanceRatios: {
            planningVsGiven: totalBudget > 0 ? (totalPlanning / totalBudget) * 100 : 0,
            actualVsPlanning: totalPlanning > 0 ? (totalActual / totalPlanning) * 100 : 0,
            savingsVsPlanning: totalPlanning > 0 ? (totalSavings / totalPlanning) * 100 : 0,
            budget_utilization: totalBudget > 0 ? (totalPlanning / totalBudget) * 100 : 0,
            spend_efficiency: totalPlanning > 0 ? (totalActual / totalPlanning) * 100 : 0,
            savings_rate: totalPlanning > 0 ? (totalSavings / totalPlanning) * 100 : 0,
            campaign_health_score: 85
          },
          dataSource: {
            summaryCards: `Snapshot ${month}`,
            costBreakdown: `Snapshot ${month}`,
            mediaTypeSavings: `Snapshot ${month}`,
            digitalVendorSavings: `Snapshot ${month}`,
            performanceTable: `Snapshot ${month}`
          }
        },
        snapshotMonth: month,
        dataSource: 'Enhanced'
      };
    } catch (error) {
      console.error('❌ Error processing snapshot data:', error);
      return {
        campaigns: [],
        brandSummaries: [],
        processedOverview: null,
        snapshotMonth: month,
        dataSource: 'Error'
      };
    }
  }

  /**
   * Safely parse float from various input types (handles Excel import quirks)
   * @param {any} value - Value to parse
   * @returns {number} Parsed float or 0
   */
  static safeParseFloat(value) {
    if (value === null || value === undefined || value === '' || value === 'None' || value === 'nan') {
      return 0;
    }
    
    try {
      const stringValue = String(value).replace(/,/g, '').trim();
      const parsed = parseFloat(stringValue);
      return isNaN(parsed) ? 0 : parsed;
    } catch (error) {
      console.warn('⚠️ Failed to parse float:', value, error);
      return 0;
    }
  }

  /**
   * Get direct snapshot data from SystemMonthlySnapshot
   * @param {string} month - Snapshot month in YYYY-MM format
   * @returns {Promise} Raw snapshot data
   */
  static async getRawSnapshotData(month) {
    try {
      console.log(`📊 Fetching raw snapshot data for month: ${month}`);
      const response = await api.get(`/system/get-snapshot-by-month/?month=${month}`);
      console.log('✅ Raw snapshot data response:', response.data);
      return response;
    } catch (error) {
      console.error('❌ Error fetching raw snapshot data:', error);
      throw error;
    }
  }

  /**
   * Clear all snapshot-related cache
   * @returns {void}
   */
  static clearSnapshotCache() {
    try {
      // Clear API cache for snapshot-related endpoints
      const cacheKeys = [
        'snapshot-months',
        'snapshot-data',
        'enhanced-metrics',
        'brand-summaries',
        'ooh-campaigns',
        'monthly-data'
      ];
      
      cacheKeys.forEach(key => {
        apiCacheService.clearCacheEntry(key);
      });
      
      console.log('🧹 Snapshot cache cleared');
    } catch (error) {
      console.warn('⚠️ Error clearing snapshot cache:', error);
    }
  }

  /**
   * Get complete snapshot data for dashboard (main method)
   * @param {string} month - Snapshot month in YYYY-MM format
   * @returns {Promise} Complete processed snapshot data
   */
  static async getSnapshotData(month) {
    try {
      console.log(`📊 Fetching complete snapshot data for dashboard: ${month}`);
      
      // First try new snapshot structure (3 tables) - PRIORITIZED
      try {
        console.log('🔄 Trying new snapshot structure (3 tables)...');
        const newSnapshotData = await this.getNewSnapshotData(month);
        console.log('📊 New snapshot data result:', {
          dataSource: newSnapshotData?.dataSource,
          hasData: !!newSnapshotData,
          isNewStructure: newSnapshotData?.dataSource === 'NewSnapshotDatabase'
        });
        
        if (newSnapshotData && newSnapshotData.dataSource === 'NewSnapshotDatabase') {
          console.log('✅ Successfully using new 3-table snapshot structure');
          return newSnapshotData;
        } else {
          console.warn('⚠️ New snapshot structure returned but dataSource is not NewSnapshotDatabase:', newSnapshotData?.dataSource);
        }
      } catch (newError) {
        console.warn('⚠️ New 3-table snapshot structure failed, falling back to old structure:', newError.message);
      }
      
      // Fallback to old JSON-based snapshot
      console.log('🔄 Falling back to old JSON snapshot structure...');
      const rawResponse = await this.getRawSnapshotData(month);
      
      if (rawResponse?.data?.snapshot) {
        console.log('📊 Processing old JSON snapshot structure...');
        // Process the snapshot data into dashboard format
        const processedData = this.processSnapshotData(rawResponse.data.snapshot, month);
        
        // Combine with enhanced metrics structure
        return {
          enhancedMetrics: {
            financial_metrics: {
              total_budget: processedData.processedOverview?.summaryCards?.givenCost || 0,
              total_planning: processedData.processedOverview?.summaryCards?.planningCost || 0,
              total_actual: processedData.processedOverview?.summaryCards?.actualCost || 0,
              total_savings: processedData.processedOverview?.summaryCards?.totalSavings || 0,
              total_campaigns: processedData.campaigns?.length || 0
            },
            brand_performance: processedData.brandSummaries || [],
            performance_ratios: processedData.processedOverview?.performanceRatios || {
              budget_utilization: 0,
              spend_efficiency: 0,
              savings_rate: 0,
              campaign_health_score: 0
            },
            data_type: 'old_snapshot',
            snapshot_month: month
          },
          brandSummaries: processedData.brandSummaries || [],
          campaigns: processedData.campaigns || [],
          oohCampaigns: processedData.campaigns?.filter(c => 
            c.media_types && Object.values(c.media_types).some(mt => 
              mt.media_type?.toLowerCase() === 'ooh'
            )
          ) || [],
          monthlyData: processedData.campaigns?.map(c => ({
            campaign_name: c.campaign_name,
            brand_name: c.brand_name,
            media_types: c.media_types
          })) || [],
          metadata: {
            snapshotId: month,
            createdAt: rawResponse.data.created_at,
            totalRecords: processedData.campaigns?.length || 0,
            note: rawResponse.data.note
          },
          dataSource: 'OldDatabase'
        };
      } else {
        console.warn('⚠️ No snapshot data found, returning empty structure');
        return this.getEmptySnapshotData(month);
      }
    } catch (error) {
      console.error('❌ Error fetching complete snapshot data:', error);
      return this.getEmptySnapshotData(month);
    }
  }

  /**
   * Get snapshot data using new structure (3 tables)
   * @param {string} month - Snapshot month in YYYY-MM format
   * @returns {Promise} Complete processed snapshot data from new structure
   */
  static async getNewSnapshotData(month) {
    try {
      console.log(`📊 Fetching new snapshot data for month: ${month}`);
      
      // Fetch all data in parallel
      const [enhancedMetrics, brandSummaries, oohCampaigns, monthlyData] = await Promise.all([
        this.getNewSnapshotEnhancedMetrics(month),
        this.getNewSnapshotBrandSummaries(month),
        this.getNewSnapshotOOHCampaigns(month),
        this.getNewSnapshotMonthlyData(month)
      ]);
      
      console.log('📊 New snapshot data fetched:', {
        enhancedMetrics: enhancedMetrics?.data ? 'Success' : 'Failed',
        brandSummaries: brandSummaries?.data ? 'Success' : 'Failed',
        oohCampaigns: oohCampaigns?.data ? 'Success' : 'Failed',
        monthlyData: monthlyData?.data ? 'Success' : 'Failed'
      });
      
      // Transform data to match current dashboard format
      const transformedData = {
        enhancedMetrics: enhancedMetrics.data?.financial_metrics || {
          total_budget: 0,
          total_planning: 0,
          total_actual: 0,
          total_savings: 0,
          total_campaigns: 0
        },
        brandSummaries: brandSummaries.data?.brand_summaries || [],
        campaigns: [], // Will be populated from brandSummaries for now
        oohCampaigns: oohCampaigns.data?.ooh_campaigns || [],
        monthlyData: monthlyData.data?.monthly_breakdown || [],
        metadata: {
          snapshotId: month,
          dataType: 'new_snapshot',
          snapshotDate: `${month}-01`,
          totalBrands: brandSummaries.data?.total_brands || 0,
          totalOOHCampaigns: oohCampaigns.data?.summary?.total_campaigns || 0
        },
        dataSource: 'NewSnapshotDatabase'
      };
      
      // Populate campaigns from brand summaries for now
      // This is a temporary solution until we have a dedicated campaigns API
      transformedData.campaigns = transformedData.brandSummaries.flatMap(brand => 
        (brand.campaigns || []).map(campaign => ({
          campaign_name: campaign.campaign_name || `${brand.brand_name} Campaign`,
          brand_name: brand.brand_name,
          sub_brand_name: brand.sub_brand_name || '',
          total_given: campaign.total_given || 0,
          planning_total: campaign.planning_total || 0,
          actual_total: campaign.actual_total || 0,
          saving_total: (campaign.planning_total || 0) - (campaign.actual_total || 0),
          efficiency_ratio: brand.efficiency_ratio || 0,
          budget_utilization: brand.budget_utilization || 0
        }))
      );
      
      console.log('✅ New snapshot data transformed successfully:', {
        enhancedMetrics: !!transformedData.enhancedMetrics,
        brandSummaries: transformedData.brandSummaries.length,
        campaigns: transformedData.campaigns.length,
        oohCampaigns: transformedData.oohCampaigns.length,
        monthlyData: transformedData.monthlyData.length
      });
      
      return transformedData;
      
    } catch (error) {
      console.error('❌ Error fetching new snapshot data:', error);
      return null;
    }
  }

  /**
   * Get empty snapshot data structure for fallback
   * @param {string} month - Snapshot month
   * @returns {Object} Empty snapshot data structure
   */
  static getEmptySnapshotData(month) {
    return {
      enhancedMetrics: {
        financial_metrics: {
          total_budget: 0,
          total_planning: 0,
          total_actual: 0,
          total_savings: 0,
          total_campaigns: 0
        },
        brand_performance: [],
        performance_ratios: {
          budget_utilization: 0,
          spend_efficiency: 0,
          savings_rate: 0,
          campaign_health_score: 0
        },
        data_type: 'snapshot_empty',
        snapshot_month: month
      },
      brandSummaries: [],
      campaigns: [],
      metadata: {
        snapshotId: month,
        createdAt: null,
        totalRecords: 0,
        note: 'No data available'
      },
      dataSource: 'Empty'
    };
  }
}

export default SnapshotDataService; 